import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { LiveChat } from './components/LiveChat';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { ScrollToTopButton } from './components/ScrollToTopButton';
import { PageTransition } from './components/PageTransition';
// Home is the landing route — keep it eager so the first paint is instant.
import { Home } from './pages/Home';
import { CartProvider } from './components/CartContext';
import { CartDrawer } from './components/CartDrawer';
import { AnnouncementBar } from './components/AnnouncementBar';
import { I18nProvider } from './utils/i18n';
// Every other route is code-split and fetched on demand, so they no longer
// weigh down the initial bundle. Named exports are mapped to a default for lazy.
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const Product = lazy(() => import('./pages/Product').then(m => ({
  default: m.Product
})));
const Project = lazy(() => import('./pages/Project').then(m => ({
  default: m.Project
})));
const HelpMeChoose = lazy(() => import('./pages/HelpMeChoose').then(m => ({
  default: m.HelpMeChoose
})));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({
  default: m.AboutPage
})));
const DeliveryZones = lazy(() => import('./pages/DeliveryZones').then(m => ({
  default: m.DeliveryZones
})));
const Blog = lazy(() => import('./pages/Blog').then(m => ({
  default: m.Blog
})));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({
  default: m.BlogPost
})));
const DeliveryPrep = lazy(() => import('./pages/DeliveryPrep').then(m => ({
  default: m.DeliveryPrep
})));
const CompareMaterials = lazy(() => import('./pages/CompareMaterials').then(m => ({
  default: m.CompareMaterials
})));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({
  default: m.NotFound
})));
const TrackOrder = lazy(() => import('./pages/TrackOrder').then(m => ({
  default: m.TrackOrder
})));
const TrackOrderLookup = lazy(() => import('./pages/TrackOrderLookup').then(m => ({
  default: m.TrackOrderLookup
})));
const Calculator = lazy(() => import('./pages/Calculator').then(m => ({
  default: m.Calculator
})));
const CityMaterial = lazy(() => import('./pages/CityMaterial').then(m => ({
  default: m.CityMaterial
})));
const ServiceAreas = lazy(() => import('./pages/ServiceAreas').then(m => ({
  default: m.ServiceAreas
})));
const LogoConcepts = lazy(() => import('./components/LogoConcepts').then(m => ({
  default: m.LogoConcepts
})));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy').then(m => ({
  default: m.RefundPolicy
})));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({
  default: m.PrivacyPolicy
})));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({
  default: m.TermsOfService
})));
const DailyTicketForm = lazy(() => import('./pages/DailyTicketForm').then(m => ({
  default: m.DailyTicketForm
})));
function RouteFallback() {
  return <div className="min-h-[60vh] flex items-center justify-center bg-[#fdfbf7] dark:bg-[#222222]" role="status" aria-label="Loading" data-id="element-0" data-mp="ydiiw5wjz7sm">
      <span className="h-8 w-8 rounded-full border-2 border-brand-orange border-t-transparent animate-spin" data-id="element-1" data-mp="1r4ir6t1x2xq3" />
    </div>;
}
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Two-pass scroll: first attempt accounts for the sticky navbar height,
      // then a follow-up corrects for late-rendering content (Services, etc.)
      // that can push the target further down the page.
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (!el) return;
        const navHeight = document.querySelector('nav')?.getBoundingClientRect().height || 80;
        const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      };
      const t1 = setTimeout(scrollToTarget, 120);
      const t2 = setTimeout(scrollToTarget, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}
function AnimatedRoutes() {
  const location = useLocation();
  return <AnimatePresence mode="wait" data-id="element-2" data-mp="zwsk2qusg66p">
      <Suspense fallback={<RouteFallback data-id="element-4" data-mp="zzoei57fgcqe" />} data-id="element-3" data-mp="zw0pnjcxgei0">
        <Routes location={location} key={location.pathname} data-id="element-5" data-mp="vwruca28o8kc">
          <Route path="/" element={<PageTransition data-id="element-7" data-mp="3knt1x1h44vpg">
                <Home data-id="element-8" data-mp="3kp410nzm5fa8" />
              </PageTransition>} data-id="element-6" data-mp="vybe4an3t1qv" />
          <Route path="/contact" element={<PageTransition data-id="element-10" data-mp="3gm7qlkk88phd">
                <Contact data-id="element-11" data-mp="3gkwf0ykjidfp" />
              </PageTransition>} data-id="element-9" data-mp="3gmh6gfbk5g9c" />
          <Route path="/shop/:slug" element={<PageTransition data-id="element-13" data-mp="1w5zxgbzvwske">
                <Product data-id="element-14" data-mp="1w2lsqx3r079s" />
              </PageTransition>} data-id="element-12" data-mp="1w4ykg1zb5w01" />
          <Route path="/projects/:slug" element={<PageTransition data-id="element-16" data-mp="203nvhnwvj1gi">
                <Project data-id="element-17" data-mp="254ld9zdz1thh" />
              </PageTransition>} data-id="element-15" data-mp="206tlr6h8o9dt" />
          <Route path="/help" element={<PageTransition data-id="element-19" data-mp="29vjud16savls">
                <HelpMeChoose data-id="element-20" data-mp="29wv91w1rpnro" />
              </PageTransition>} data-id="element-18" data-mp="2530hk6fx84i1" />
          <Route path="/about" element={<PageTransition data-id="element-22" data-mp="2evppwsg6d295">
                <AboutPage data-id="element-23" data-mp="2eueqt5xociod" />
              </PageTransition>} data-id="element-21" data-mp="2euwrqfva7l2m" />
          <Route path="/delivery-zones" element={<PageTransition data-id="element-25" data-mp="2jsg8pnjd6by9">
                <DeliveryZones data-id="element-26" data-mp="2nvl7pg48jvi5" />
              </PageTransition>} data-id="element-24" data-mp="2jthqeauvxwoy" />
          <Route path="/subcontractors" element={<Navigate to="/" replace data-id="element-28" data-mp="2supdrxvv5d49" />} data-id="element-27" data-mp="2nw4crw8seper" />
          <Route path="/contractor-ordering" element={<Navigate to="/" replace data-id="element-30" data-mp="2ssbm7z0e6j1g" />} data-id="element-29" data-mp="2stmqdzxr1st3" />
          <Route path="/diy-help" element={<Navigate to="/blog/perfect-gravel-driveway" replace data-id="element-32" data-mp="tj4u1u941f39" />} data-id="element-31" data-mp="tid8lc988xqp" />
          <Route path="/blog" element={<PageTransition data-id="element-34" data-mp="yh6lb2gowkpt">
                <Blog data-id="element-35" data-mp="yihob02b9ofv" />
              </PageTransition>} data-id="element-33" data-mp="yfv8ye96i4n3" />
          <Route path="/blog/:slug" element={<PageTransition data-id="element-37" data-mp="21tojq66hn39e">
                <BlogPost data-id="element-38" data-mp="1wt0spki9emf6" />
              </PageTransition>} data-id="element-36" data-mp="21sddlzpkljf4" />
          <Route path="/delivery-prep" element={<PageTransition data-id="element-40" data-mp="2as0k2zx5pff1">
                <DeliveryPrep data-id="element-41" data-mp="2at1t6yum3ru4" />
              </PageTransition>} data-id="element-39" data-mp="1wubytqz6g69g" />
          <Route path="/compare" element={<PageTransition data-id="element-43" data-mp="25s4bendikzt5">
                <CompareMaterials data-id="element-44" data-mp="25ruo57jut6r5" />
              </PageTransition>} data-id="element-42" data-mp="25tfgcqip4dlu" />
          <Route path="/calculator" element={<PageTransition data-id="element-46" data-mp="2kobvlnoxa18f">
                <Calculator data-id="element-47" data-mp="2frlfj0d4b5n0" />
              </PageTransition>} data-id="element-45" data-mp="2kpemhvm8w3oc" />
          {/* "Why shop local" is now the second half of the About page, so the
              old standalone URL redirects there rather than 404ing. */}
          <Route path="/local-vs-national" element={<Navigate to="/about" replace data-id="element-49" data-mp="2phogpnue7pwf" />} data-id="element-48" data-mp="2pgmxux74xz45" />
          <Route path="/track" element={<PageTransition data-id="element-51" data-mp="33hzaozr01klt">
                <TrackOrderLookup data-id="element-52" data-mp="33h7i7xsp82zr" />
              </PageTransition>} data-id="element-50" data-mp="33go5qwlti6t4" />
          <Route path="/track/:orderId" element={<PageTransition data-id="element-54" data-mp="2yfqgy16ayein">
                <TrackOrder data-id="element-55" data-mp="3i3wfdszha42z" />
              </PageTransition>} data-id="element-53" data-mp="2yiw43avdf6bq" />
          <Route path="/service-areas" element={<PageTransition data-id="element-57" data-mp="3n2fzdnuih5sp">
                <ServiceAreas data-id="element-58" data-mp="3n1enxi9l2hag" />
              </PageTransition>} data-id="element-56" data-mp="3i4ek7e07hqmi" />
          <Route path="/logo-concepts" element={<PageTransition data-id="element-60" data-mp="384nlf04m4pgm">
                <LogoConcepts data-id="element-61" data-mp="387jikm8mza40" />
              </PageTransition>} data-id="element-59" data-mp="385p8y42tf4f8" />
          <Route path="/refund-policy" element={<PageTransition data-id="element-63" data-mp="3d6mosa0crmdk">
                <RefundPolicy data-id="element-64" data-mp="2zdevmdwmiitr" />
              </PageTransition>} data-id="element-62" data-mp="3d3igp1jro6hm" />
          <Route path="/privacy-policy" element={<PageTransition data-id="element-66" data-mp="344dgzrwgy73v">
                <PrivacyPolicy data-id="element-67" data-mp="344n9pn8wesd9" />
              </PageTransition>} data-id="element-65" data-mp="2zb27fj09vc89" />
          <Route path="/terms-of-service" element={<PageTransition data-id="element-69" data-mp="2pgacyo1zl5hp">
                <TermsOfService data-id="element-70" data-mp="2pd4jkwmbrhg6" />
              </PageTransition>} data-id="element-68" data-mp="2pf7kibp0muzo" />
          <Route path="/form" element={<PageTransition data-id="element-72" data-mp="2uby06xwwrokk">
                <DailyTicketForm data-id="element-73" data-mp="2gej274sla8gz" />
              </PageTransition>} data-id="element-71" data-mp="2uety4m8rad90" />

          <Route path="/gravel-delivery/:citySlug/:materialSlug" element={<PageTransition data-id="element-75" data-mp="2ketcuurbtn26">
                <CityMaterial data-id="element-76" data-mp="2khyz828kmau7" />
              </PageTransition>} data-id="element-74" data-mp="2ggdqmt32kipt" />
          <Route path="*" element={<PageTransition data-id="element-78" data-mp="30ulzruvagntm">
                <NotFound data-id="element-79" data-mp="2vwapjj130bav" />
              </PageTransition>} data-id="element-77" data-mp="30uc9e66c0ene" />
        </Routes>
      </Suspense>
    </AnimatePresence>;
}
function AppContent({
  isDark,
  toggleDark
}: {
  isDark: boolean;
  toggleDark: () => void;
}) {
  const location = useLocation();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  // The daily ticket form (/form) is a standalone operational document. It
  // renders bare — no site chrome (navbar, footer, announcement bar, live
  // chat, sticky CTA, cart) — so it prints clean and stays out of the way.
  const isStandalonePage = location.pathname === '/form';
  if (isStandalonePage) {
    // The daily ticket is a printed white document — never apply the `dark`
    // class here so it always renders on a clean white surface regardless of
    // the visitor's theme.
    return <div className="min-h-screen font-sans" data-id="element-80" data-mp="1u7qh5q9lcv4d">
        <ScrollToHash data-id="element-81" data-mp="9p63pxw0qbr6" />
        <AnimatedRoutes data-id="element-82" data-mp="9nusxe45o3qk" />
      </div>;
  }
  return <div className={`min-h-screen flex flex-col font-sans ${isDark ? 'dark' : ''} ${isContractorPage ? 'contractor-theme' : ''}`} data-id="element-83" data-mp="9r8uwl8wqaxh">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-brand-orange focus:text-white focus:rounded-lg focus:font-bold focus:outline-none focus:ring-2 focus:ring-white" data-id="element-84" data-mp="ip2qftyul0jl">
        Skip to main content
      </a>
      <div className="flex-grow bg-[#fdfbf7] dark:bg-[#222222] transition-colors duration-300 pb-16 md:pb-0" id="main-content" data-id="element-85" data-mp="3r4memeknvsrs">
        <ScrollToHash data-id="element-86" data-mp="3w2wakre7mxeg" />
        <AnnouncementBar data-id="element-87" data-mp="3w2e7bat4rix1" />
        <Navbar isDark={isDark} toggleDark={toggleDark} data-id="element-88" data-mp="4pjip29y7r7t" />
        <AnimatedRoutes data-id="element-89" data-mp="4r4bongm723k" />
        <Footer data-id="element-90" data-mp="4rmey41p2gkz" />
        <LiveChat data-id="element-91" data-mp="33g39g7wwrw3x" />
        <StickyMobileCTA data-id="element-92" data-mp="33gl81b71mmf0" />
        <ScrollToTopButton data-id="element-93" data-mp="33fa67gx5roqj" />
        <CartDrawer data-id="element-94" data-mp="33dz21fzsweyw" />
      </div>
    </div>;
}
// Decide the initial theme for a first-time visitor (no saved preference yet):
//   1. An explicit saved choice always wins — a returning visitor who picked a
//      theme keeps it.
//   2. Everyone else starts in LIGHT mode.
//
// Light is the deliberate default. Customers are contractors and homeowners
// pricing material outdoors on a phone in daylight, so light mode is the
// higher-value surface and the one held to the tightest design standard. The
// previous behavior deferred to the OS `prefers-color-scheme` and then to a
// time-of-day clock rule, which meant a majority of first-time visitors landed
// in dark mode by accident — and made the very first impression the mode that
// gets less design attention. The toggle still switches modes and still
// persists, so nothing is taken away from anyone who prefers dark.
function getInitialTheme(): boolean {
  try {
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
  } catch {
    // localStorage can throw in private mode / embedded webviews / when
    // storage is blocked. Fall through to the light default.
  }
  return false;
}
export function App() {
  const [isDark, setIsDark] = useState(getInitialTheme);
  useEffect(() => {
    try {
      localStorage.setItem('theme', isDark ? 'dark' : 'light');
    } catch {}
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);
  const toggleDark = () => setIsDark(!isDark);
  return <I18nProvider data-id="element-95" data-mp="389xz1gfkwye2">
      <Router data-id="element-96" data-mp="389ed7omt5pur">
        <CartProvider data-id="element-97" data-mp="388v6z56ispwk">
          <AppContent isDark={isDark} toggleDark={toggleDark} data-id="element-98" data-mp="388d59t139jh9" />
        </CartProvider>
      </Router>
    </I18nProvider>;
}